import CampaignPlatformType from '@/Enums/CampaignManagement/CampaignPlatformType.enum';
import DateFormat from '@/Enums/Format/DateFormat.enum';
import ClearAllButton from '@/components/Buttons/ClearAllButton';
import DateRangeWithShortcuts from '@/components/DateRangeWithShortcuts';
import FiltersBadge from '@/components/Filter/Badge';
import LightningIcon from '@/components/Icons/LightningIcon';
import MegaphoneIcon from '@/components/Icons/MegaphoneIcon';
import TvIcon from '@/components/Icons/TvIcon';
import UserIcon from '@/components/Icons/UserIcon';
import SelectInputPlaceholder from '@/components/Select/InputPlaceholder';
import SearchSelect from '@/components/Select/SearchSelect';
import { LEVEL_LABELS, LEVEL_OPTIONS, STATUS_LABELS, STATUS_OPTIONS } from '@/constants/campaignManagement';
import { useAppSelector } from '@/store/hooks';
import { selectIsDV360CampaignManagementEnabled } from '@/store/selectors/currentWhitelabel.selectors';
import { ArrowDropDown } from '@mui/icons-material';
import PublicIcon from '@mui/icons-material/Public';
import { Checkbox, ListItemText, MenuItem, OutlinedInput, Paper, Select } from '@mui/material';
import moment from 'moment';
import PropTypes from 'prop-types';
import { useCallback, useMemo } from 'react';
import FilterPopover from './FilterPopover';
import SavedViewsMenu from './SavedViewsMenu';

const getInputValue = value => value.map(item => item.label).join(', ');

const Filter = ({ filters, setFilters, resetFilters, clients }) => {
  const isDV360CampaignManagementEnabled = useAppSelector(selectIsDV360CampaignManagementEnabled);

  const {
    status,
    pacingStatus,
    level,
    platform,
    clients: filterClients,
    campaigns: filterCampaigns,
    advertisers: filterAdvertisers,
    channelTypes: filterChannelTypes,
    dateRangeStart,
    dateRangeEnd,
  } = filters;

  const setStatus = useCallback(
    newStatus => {
      setFilters({
        status: newStatus,
      });
    },
    [setFilters],
  );

  const setLevel = useCallback(
    newLevel => {
      setFilters({
        level: newLevel,
      });
    },
    [setFilters],
  );

  const setPlatform = useCallback(
    newPlatform => {
      if (!isDV360CampaignManagementEnabled) {
        return;
      }

      setFilters({
        platform: newPlatform,
      });
    },
    [setFilters, isDV360CampaignManagementEnabled],
  );

  const setClients = useCallback(
    newClients => {
      setFilters({
        clients: newClients,
      });
    },
    [setFilters],
  );

  const setAdvertisers = useCallback(
    newAdvertisers => {
      setFilters({
        advertisers: newAdvertisers,
      });
    },
    [setFilters],
  );

  const setDateRange = useCallback(
    range => {
      const newStartDate = range[0]?.format(DateFormat.Iso8601);
      const newEndDate = range[1]?.format(DateFormat.Iso8601);
      if (dateRangeStart !== newStartDate || dateRangeEnd !== newEndDate) {
        setFilters({
          dateRangeStart: newStartDate,
          dateRangeEnd: newEndDate,
        });
      }
    },
    [setFilters, dateRangeStart, dateRangeEnd],
  );

  const dateRangeValue = useMemo(() => [moment(dateRangeStart), moment(dateRangeEnd)], [dateRangeStart, dateRangeEnd]);

  const filterCount = useMemo(
    () =>
      [
        level ? 1 : 0,
        pacingStatus?.length || 0,
        status?.length || 0,
        filterClients?.length || 0,
        filterAdvertisers?.length || 0,
        filterCampaigns?.length || 0,
        filterChannelTypes?.length || 0,
      ].reduce((sum, curr) => sum + curr, 0),
    [filterCampaigns, filterClients, level, pacingStatus, status, filterAdvertisers, filterChannelTypes],
  );

  const advertisers = useMemo(() => {
    return filterClients
      .map(client => client.advertisers)
      .flat()
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [filterClients]);

  return (
    <div className='p-7'>
      <Paper className='py-6 px-7 flex flex-col gap-y-[9px]'>
        <div className='flex items-center justify-between'>
          <div className='flex gap-[9px] items-center'>
            <span className='text-[24px] font-bold'>Filter</span>
            <FiltersBadge
              count={filterCount}
              sx={{
                minWidth: '26px',
                width: 'unset',
                height: '26px',
                fontSize: '14px',
              }}
            />
          </div>
          <div className='flex items-center gap-[9px]'>
            <SavedViewsMenu />
            <ClearAllButton onClick={resetFilters} />
          </div>
        </div>
        <div className='flex gap-[20px] flex-wrap xl:flex-nowrap'>
          {isDV360CampaignManagementEnabled && (
            <Select
              value={platform}
              displayEmpty
              renderValue={value => value || <SelectInputPlaceholder>Platform</SelectInputPlaceholder>}
              startAdornment={<PublicIcon />}
              slotProps={{
                paper: { className: 'mt-2' },
              }}
              sx={{ flex: '1 1 50px' }}
              onChange={e => setPlatform(e.target.value)}
            >
              {Object.values(CampaignPlatformType).map(option => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          )}
          <SearchSelect
            closeOnClickOutside={true}
            dimBackground={false}
            value={filterClients}
            onChange={setClients}
            options={clients}
            label='Clients'
            getOptionLabel={option => option.label}
            tabSelectSx={{ width: '300px' }}
            renderInput={props => (
              <OutlinedInput
                readOnly
                startAdornment={<TvIcon className='w-[20px] shrink-0' />}
                endAdornment={<ArrowDropDown />}
                placeholder='Select Client'
                className='flex-1 min-w-[200px]'
                value={getInputValue(filterClients)}
                {...props}
              />
            )}
          />
          <SearchSelect
            closeOnClickOutside={true}
            dimBackground={false}
            value={filterAdvertisers}
            onChange={setAdvertisers}
            options={advertisers}
            label='Advertiser'
            getOptionLabel={option => option.label}
            tabSelectSx={{ width: '300px' }}
            renderInput={props => (
              <OutlinedInput
                readOnly
                startAdornment={<UserIcon className='w-[20px] shrink-0' />}
                endAdornment={<ArrowDropDown />}
                placeholder='Select Advertiser'
                className='flex-1 min-w-[240px]'
                disabled={!filterClients?.length}
                value={getInputValue(filterAdvertisers)}
                {...props}
              />
            )}
          />
          <div className='flex gap-[20px] flex-wrap xl:flex-nowrap'>
            <DateRangeWithShortcuts value={dateRangeValue} onChange={setDateRange} />
          </div>
          <Select
            startAdornment={<MegaphoneIcon className='w-[30px]' />}
            value={status}
            multiple
            displayEmpty
            renderValue={selectValue =>
              !status?.length ? (
                <SelectInputPlaceholder>Status</SelectInputPlaceholder>
              ) : (
                <>{selectValue.map(val => STATUS_LABELS[val]).join(', ')}</>
              )
            }
            slotProps={{
              paper: { className: 'mt-2' },
            }}
            sx={{
              flex: 1,
            }}
            onChange={e => setStatus(e.target.value)}
          >
            {STATUS_OPTIONS.map((option, i) => (
              <MenuItem key={i} value={option.value} sx={{ paddingLeft: '4px' }}>
                <Checkbox checked={status.includes(option.value)} />
                <ListItemText>{option.label}</ListItemText>
              </MenuItem>
            ))}
          </Select>
          <Select
            startAdornment={<LightningIcon className='w-[30px]' />}
            value={level}
            displayEmpty
            renderValue={selectValue =>
              !level ? <SelectInputPlaceholder>Level</SelectInputPlaceholder> : <>{LEVEL_LABELS[selectValue]}</>
            }
            slotProps={{
              paper: { className: 'mt-2' },
            }}
            sx={{
              flex: 1,
            }}
            onChange={e => setLevel(e.target.value)}
          >
            {LEVEL_OPTIONS.map((option, i) => (
              <MenuItem key={i} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </div>
        <div className='flex flex-wrap gap-[9px] items-center'>
          <FilterPopover filters={filters} setFilters={setFilters} />
        </div>
      </Paper>
    </div>
  );
};

Filter.propTypes = {
  filters: PropTypes.object,
  setFilters: PropTypes.func,
  resetFilters: PropTypes.func,
  clients: PropTypes.array,
};

export default Filter;
